import React, { useContext, useEffect, useRef, useState } from 'react';
import gsap, { Cubic } from 'gsap';
import PropTypes from 'prop-types';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { CruiseSelectionContext } from 'contexts/CruiseSelectionContext';
import { useConfig } from 'hooks';

import styled from 'styled-components';
import colors from 'styles/colors';
import fonts from 'styles/fonts';
import sizes from 'styles/sizes';
import media from 'styles/media';

import PromoCountdown from './PromoCountdown';
import { PROMO_ID } from './constants';
import { BRANDS, BRAND_RCI } from 'contexts/CruiseSearchContext/constants';
import { PATH_PROMOTION_TERMS_AND_CONDITIONS } from 'lib/constants';

import { getPerksInfo } from 'lib/targetedOffers/Offers';
import { useFeatureFlags } from '../../hooks/LaunchDarkly/useFeatureFlags';

const Wrapper = styled.div`
	margin: 0 auto;
	${(props) => props.addMarginBottom && 'margin-bottom: 20px;'}
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	position: relative;
	padding: 0 ${sizes.gutter.large}px;
	${media.medium`
    padding: 0 ${sizes.gutter.small}px;
  `}
`;

const LockupImg = styled.img`
	display: flex;
	margin-left: auto;
	margin-right: auto;
	max-width: 500px;
	max-height: 100px;
	object-fit: cover;
	@media (max-width: ${sizes.media.smallMaxWidth}px) {
		max-width: 335px;
		max-height: 150px;
	}
`;

const Header = styled.p`
	text-align: center;
	${(props) =>
		props.perkLockupStyle
			? `
    font-family: ${font_family(props.brand)};
    font-size: 20px;
    line-height: ${line_height_desktop(props.brand)};
    text-transform: ${text_transform(props.brand)};
    color: ${color(props.brand)};
    @media (max-width: ${sizes.media.tabletMinWidth}px) {
      font-size: ${font_size_mobile(props.brand)};
      line-height: ${line_height_mobile(props.brand)};
    } 
  `
			: `
    font-family: ${fonts.secondary.optimized};
    font-size: 64px;
    line-height: 79px;
    text-transform: uppercase;
    color: ${colors.secondary.deepCove};
    margin-bottom: 0px;
    min-height: ${(props) => (props.isPromo ? '48px' : 'unset')} 
    ${media.small`
      font-size: 36px;    
  `};
    @media (max-width: ${sizes.media.smallMaxWidth}px) {
      font-size: 56px;
      line-height: 100%;
      margin-top: 10px;
    }
  `}
`;

const Perks = styled.p`
	text-align: center;
	${(props) =>
		props.perkLockupStyle
			? `
    font-family: ${font_family(props.brand)};
    font-size: 20px;
    line-height: ${line_height_desktop(props.brand)};
    text-transform: ${text_transform(props.brand)};
    color: ${color(props.brand)};
    @media (max-width: ${sizes.media.tabletMinWidth}px) {
      font-size: ${font_size_mobile(props.brand)};
      line-height: ${line_height_mobile(props.brand)};
    } 
  `
			: `
    font-family: ${fonts.primary.regular};
    font-size: 18px;
    line-height: 125%;
    color: ${colors.functional.softGrey};
    ${media.small`
      font-size: 14px;    
    `}
    @media (max-width: ${sizes.media.smallMaxWidth}px) {
      font-size: 18px;
      line-height: 26px;
    }
  `}
`;

const TermsAndConditions = styled.a`
	font-size: 12px;
	color: ${colors.primary.ocean};
	align-self: flex-end;
	font-style: italic;
	text-decoration: underline;
	line-height: 18px;
	opacity: 0;
`;

const font_family = (brand) => {
	return brand == BRAND_RCI.code ? fonts.primary.bold : fonts.celebrity.neutraface;
};

const line_height_desktop = (brand) => {
	return brand == BRAND_RCI.code ? '29px' : '30px';
};

const line_height_mobile = (brand) => {
	return brand == BRAND_RCI.code ? '26px' : '24px';
};

const color = (brand) => {
	return brand == BRAND_RCI.code ? colors.secondary.deepCove : colors.celebrity.grey;
};

const text_transform = (brand) => {
	return brand == BRAND_RCI.code ? 'none' : 'uppercase';
};

const font_size_mobile = (brand) => {
	return brand == BRAND_RCI.code ? '18px' : '16px';
};

const WithPromotion = ({ passedRef, perkTexts, countdownEnabled, extended, endDate, promotionLinkPath, promotionLinkLabel, lockup }) => {
	const termsRef = useRef();
	const { baseUrl, brand } = useConfig();

	const { isMobile } = useContext(CruiseSelectionContext);

	const [perkLockupStyle, setPerkLockupStyle] = useState(!!lockup);

	const lockupPath = lockup && `${baseUrl}${isMobile ? lockup.mobile.relativePath : lockup.desktop.relativePath}`;

	const checkErrorImg = (event) => {
		event.target.style.display = 'none';
		setPerkLockupStyle(false);
	};

	useEffect(() => {
		const tl = gsap.timeline();
		gsap.config({
			nullTargetWarn: false,
			trialWarn: false,
		});

		tl.to(termsRef.current, {
			opacity: 0,
			y: 0,
			duration: 0.4,
		}).to(termsRef.current, {
			opacity: 1,
			y: 0,
			duration: 0.4,
			stagger: 0.1,
			ease: Cubic.easeInOut(),
		});
	}, []);

	const hadLockup = lockup !== null;

	useEffect(() => {
		setPerkLockupStyle(!!lockup);
	}, [!!lockup]);

	return (
		<>
			<div ref={passedRef}>
				{hadLockup && <LockupImg src={lockupPath} onError={(e) => checkErrorImg(e)} />}
				{perkTexts.map((perk, index) =>
					index === 0 && !hadLockup ? (
						<Header key={`${perk}-header`} perkLockupStyle={perkLockupStyle} brand={brand.code}>
							{perk}
						</Header>
					) : (
						<Perks key={`${perk}-item`} perkLockupStyle={perkLockupStyle} brand={brand.code}>{`+ ${perk}`}</Perks>
					),
				)}
			</div>
			{countdownEnabled && <PromoCountdown extended={extended} endDate={endDate} perkLockupStyle={perkLockupStyle} brand={brand.code} />}
			<TermsAndConditions ref={termsRef} target="_blank" href={promotionLinkPath}>
				{promotionLinkLabel}
			</TermsAndConditions>
		</>
	);
};

WithPromotion.propTypes = {
	passedRef: PropTypes.object,
	perkTexts: PropTypes.array,
	countdownEnabled: PropTypes.bool,
	extended: PropTypes.bool,
	endDate: PropTypes.string,
	termsRef: PropTypes.object,
	promotionLinkPath: PropTypes.string,
	promotionLinkLabel: PropTypes.string,
	lockup: PropTypes.object,
};

const WithOutPromotion = ({ passedRef, title, promoLoaded }) => (
	<>
		<div ref={passedRef}>
			<Header isPromo={promoLoaded}>{title}</Header>
		</div>
	</>
);

WithOutPromotion.propTypes = {
	passedRef: PropTypes.object,
	title: PropTypes.string,
	promoLoaded: PropTypes.any,
};

/**
 * Promo
 *
 * The logic for this component is not complete.
 * UI for this component has been built out and the countdown functionality is working, but its business logic needs to be updated.
 *
 * @param {object} passedRef - ?
 * @param {object} mockPromo - mock data for rendering this component.
 *
 * @typedef {Object} mockPromo
 * @property {boolean} countdownEnabled - should the Promo render the PromoCountdown component
 * @property {string} endDate - Format: '2022-03-29T03:55:04.559Z'; determines how much time is left on the countdown.
 */
function Promo({ passedRef, mockPromo }) {
	const { promo, loadedPromo, brand, globalization, locale, promotionsApplied } = useContext(CruiseSearchContext);
	const { showSearchBar, showPromotionBanner } = useFeatureFlags();
	const [currentPromo, setCurrentPromo] = useState(promo);
	const termsPromotionLinkPath = `${locale?.path || '/'}${PATH_PROMOTION_TERMS_AND_CONDITIONS}`;

	const { generalDictionary } = globalization?.dictionaries || {};

	const { actionExclusionsApply, labelPromoHeroDefaultTitle } = generalDictionary;

	const getAttributesPromo = (promo) => {
		const { endDate, countdownEnabled, perks, extended, lockup } = promo || {};
		return {
			endDate,
			countdownEnabled,
			perks,
			extended,
			lockup,
		};
	};

	const getCurrentPromo = (promo, promotionsApplied) => {
		const { valid, status } = promotionsApplied || {};
		if (valid && status === 'active') {
			return getAttributesPromo(promotionsApplied);
		}
		return getAttributesPromo(promo);
	};

	useEffect(() => {
		setCurrentPromo(getCurrentPromo(promo, promotionsApplied));
	}, [promo, promotionsApplied]);

	const { endDate, countdownEnabled, perks, extended, lockup } = currentPromo || mockPromo || {};

	const { listDescriptions: perkTexts } = getPerksInfo(perks);
	const { alpha3Code: brandCode } = BRANDS.find((b) => b.code === brand);

	const hasPromo = () => (Object.keys(currentPromo || {}).length === 0 ? false : true);

	return showPromotionBanner ? (
		<Wrapper id={PROMO_ID} addMarginBottom={showSearchBar}>
			{hasPromo() ? (
				<WithPromotion
					passedRef={passedRef}
					perkTexts={perkTexts}
					countdownEnabled={countdownEnabled}
					extended={extended}
					endDate={endDate}
					promotionLinkPath={termsPromotionLinkPath}
					promotionLinkLabel={actionExclusionsApply}
					lockup={lockup}
					brand={brand}
				/>
			) : (
				<WithOutPromotion passedRef={passedRef} title={loadedPromo ? labelPromoHeroDefaultTitle : null} promoLoaded={loadedPromo} />
			)}
		</Wrapper>
	) : (
		<div id={`${brandCode}-promotion-banner`}></div>
	);
}

Promo.propTypes = {
	passedRef: PropTypes.object,
	mockPromo: PropTypes.shape({
		countdownEnabled: PropTypes.bool,
		endDate: PropTypes.string,
	}),
};

export default Promo;
